import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getComplianceSummary, getOrgCompliancePosture } from '../../lib/api';
import { useFeature } from '../../lib/useFeature';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

/** Small conic-gradient posture ring in the brand teal. */
function ScoreRing({ score }: { score: number }) {
  return (
    <div
      className="grid h-16 w-16 flex-none place-items-center rounded-full"
      style={{ background: `conic-gradient(var(--viridian) ${score}%, var(--surface-3) 0)` }}
      role="img"
      aria-label={`Posture score ${score} of 100`}
    >
      <div className="grid h-[50px] w-[50px] place-items-center rounded-full bg-surface">
        <span className="font-display text-lg font-bold leading-none tabular-nums">{score}</span>
      </div>
    </div>
  );
}

/**
 * Domain dashboard posture widget (DASH + NIS2). Renders only when the compliance
 * lens is rolled out AND enabled on this domain — otherwise it self-hides, so the
 * dashboard is unchanged for domains without compliance. Shares the ['compliance',
 * domainId] query key with the Compliance page (no double fetch).
 */
export function DomainCompliancePosture({ domainId }: { domainId: string }) {
  const on = useFeature('compliance');
  const q = useQuery({
    queryKey: ['compliance', domainId],
    queryFn: () => getComplianceSummary(domainId),
    enabled: on && !!domainId,
  });
  const posture = q.data?.posture;
  if (!on || !posture?.enabled) return null;

  return (
    <Card className="p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="font-semibold">NIS2 compliance posture</h2>
        <Link
          to={`/domains/${domainId}/compliance`}
          className="text-xs font-semibold text-iris hover:text-iris-strong"
        >
          View NIS2 compliance →
        </Link>
      </div>
      <p className="mb-3 text-xs text-muted">
        Score 0–100: the weighted share of NIS2 control checks your resources pass.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <ScoreRing score={posture.score} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            <Badge variant={posture.gapCount > 0 ? 'danger' : 'success'} dot>
              {posture.gapCount} {posture.gapCount === 1 ? 'gap' : 'gaps'}
            </Badge>
            {posture.criticalGapCount > 0 && (
              <Badge variant="danger" dot>
                {posture.criticalGapCount} critical
              </Badge>
            )}
            <Badge variant="neutral" dot>
              {posture.assetsInScope} assets
            </Badge>
          </div>
          {posture.topGaps.length > 0 && (
            <ul className="mt-2.5 space-y-1 text-xs text-muted">
              {posture.topGaps.slice(0, 3).map((g) => (
                <li key={g.id} className="truncate">
                  <span className="text-haze">{g.summary}</span>
                  <span className="font-mono text-[0.62rem]"> · {g.resourceName}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Org dashboard posture widget — the weighted score across every compliance-enabled
 * domain the user can read, with a per-domain breakdown. Self-hides when the lens
 * isn't rolled out or no domain has it on.
 */
export function OrgCompliancePosture() {
  const on = useFeature('compliance');
  const q = useQuery({
    queryKey: ['org-compliance'],
    queryFn: getOrgCompliancePosture,
    enabled: on,
  });
  const data = q.data;
  if (!on || !data?.enabled) return null;

  return (
    <Card className="p-5">
      <h2 className="font-semibold">NIS2 compliance posture</h2>
      <p className="mb-3 mt-1 text-xs text-muted">
        Score 0–100 across compliance-enabled domains: the weighted share of NIS2 control checks
        your resources pass.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <ScoreRing score={data.score} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            <Badge variant={data.gapCount > 0 ? 'danger' : 'success'} dot>
              {data.gapCount} {data.gapCount === 1 ? 'gap' : 'gaps'}
            </Badge>
            {data.criticalGapCount > 0 && (
              <Badge variant="danger" dot>
                {data.criticalGapCount} critical
              </Badge>
            )}
            <Badge variant="neutral" dot>
              {data.domainsEnabled} {data.domainsEnabled === 1 ? 'domain' : 'domains'}
            </Badge>
          </div>
          <ul className="mt-2.5 divide-y divide-hairline">
            {data.domains.map((d) => (
              <li
                key={d.domainId}
                className="flex items-center justify-between gap-2 py-1.5 text-sm"
              >
                <Link
                  to={`/domains/${d.domainId}/compliance`}
                  className="truncate text-haze hover:text-bone"
                >
                  {d.name}
                </Link>
                <span className="flex flex-none items-center gap-2 font-mono text-xs">
                  {d.criticalGapCount > 0 && (
                    <span className="text-danger">{d.criticalGapCount} crit</span>
                  )}
                  <span className="font-bold tabular-nums">{d.score}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}
